import { StyledSelect } from "../primitives/styled-select";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Copy } from "lucide-react";
import type * as React from "react";
import { cn } from "../lib/cn";
import { Checkbox } from "../primitives/checkbox";
import { type FieldSpec, type WizardState, localize, localizeOpt } from "./types";

/**
 * FieldRenderer — one input per `FieldSpec.type`.
 *
 * Sibling of `@ando/ui/ando/ActionFieldEditor` — that one is the HITL
 * action editor (renders proposed action fields + citation badges); this
 * one is leaner because connector wizards never need citation hints or
 * action-time recipient prominence.
 *
 * We intentionally do NOT call into `ActionFieldEditor` because:
 *   • action editors carry an `ActionFieldSpec.citation` slot for AI
 *     extraction provenance that doesn't apply to wizards;
 *   • action editors validate against schemas fetched at runtime, wizards
 *     validate on `required` + `type` only;
 *   • action editors emit zod-shaped errors, wizards emit simple strings.
 * The two stay decoupled and DRY *within their domain* — the alternative
 * (one mega-renderer) would force every wizard to ship the action-editor
 * deps.
 */

const inputCls =
  "w-full rounded-md border border-hairline bg-card px-2.5 py-1.5 text-sm text-ink-1 placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

interface FieldRendererProps {
  readonly spec: FieldSpec;
  readonly value: unknown;
  readonly disabled?: boolean;
  readonly onChange: (next: unknown) => void;
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  spec,
  value,
  disabled,
  onChange,
}) => {
  const { i18n } = useLingui();
  const id = `wzd-fld-${spec.name}`;
  const labelText = localize(spec.label, i18n);
  const helperText = localizeOpt(spec.helperText, i18n);
  const placeholderText = localizeOpt(spec.placeholder, i18n);
  const labelNode = (
    <label htmlFor={id} className="flex items-center gap-1 font-medium text-ink-2 text-xs">
      <span>{labelText}</span>
      {spec.required ? <span className="text-accent">*</span> : null}
    </label>
  );
  const helper = helperText ? <p className="text-[11px] text-ink-3">{helperText}</p> : null;

  // Callback URL — read-only with copy button (v1 BYOA SF pattern).
  if (spec.type === "callback_readonly") {
    const stringValue = typeof value === "string" ? value : "";
    return (
      <div className="flex flex-col gap-1">
        {labelNode}
        <div className="flex items-center gap-2">
          <input
            id={id}
            value={stringValue}
            readOnly
            className={cn(inputCls, "overflow-x-auto font-mono text-xs")}
            aria-readonly
          />
          <button
            type="button"
            onClick={() => {
              if (typeof navigator !== "undefined" && navigator.clipboard && stringValue) {
                void navigator.clipboard.writeText(stringValue);
              }
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-hairline bg-card text-ink-2 hover:bg-card/80"
            aria-label={i18n._(msg`Copia URL`)}
          >
            <Copy className="h-4 w-4" aria-hidden />
          </button>
        </div>
        {helper}
      </div>
    );
  }

  if (spec.type === "select") {
    const stringValue = typeof value === "string" ? value : "";
    return (
      <div className="flex flex-col gap-1">
        {labelNode}
        <StyledSelect
          id={id}
          value={stringValue}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={cn(inputCls)}
        >
          {!spec.required ? <option value="">—</option> : null}
          {(spec.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {localize(opt.label, i18n)}
            </option>
          ))}
        </StyledSelect>
        {helper}
      </div>
    );
  }

  if (spec.type === "boolean") {
    return (
      <label className="flex cursor-pointer select-none items-center gap-2" htmlFor={id}>
        <Checkbox
          id={id}
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-ink-2 text-sm">{labelText}</span>
      </label>
    );
  }

  const inputType =
    spec.type === "password"
      ? "password"
      : spec.type === "email"
        ? "email"
        : spec.type === "number"
          ? "number"
          : spec.type === "url"
            ? "url"
            : "text";

  return (
    <div className="flex flex-col gap-1">
      {labelNode}
      <input
        id={id}
        type={inputType}
        value={value == null ? "" : String(value)}
        placeholder={placeholderText}
        required={spec.required}
        disabled={disabled}
        onChange={(e) => onChange(spec.type === "number" ? Number(e.target.value) : e.target.value)}
        className={cn(inputCls)}
      />
      {helper}
    </div>
  );
};
FieldRenderer.displayName = "FieldRenderer";

/**
 * Validate a list of FieldSpec against the current wizard state. Returns
 * `null` when all required fields are populated, otherwise the first error.
 * The ``i18n`` argument resolves any ``MessageDescriptor`` labels at call
 * time so the error string reflects the user's active locale.
 */
export function validateFields(
  fields: readonly FieldSpec[],
  state: WizardState,
  i18n: Parameters<typeof localize>[1],
): string | null {
  for (const f of fields) {
    if (!f.required) continue;
    const raw = state[f.name];
    if (raw === undefined || raw === null || raw === "") {
      return i18n._(msg`Campo obbligatorio: ${localize(f.label, i18n)}`);
    }
  }
  return null;
}
